import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CONSTANTS_DETAILS } from '../../shared/constant';
import {
  Category,
  Country,
  RecipeSearchPayload,
  RecipeSearchResponse,
  Recipe,
  RecipeCreatePayload,
  RecipeDetailsResponse,
  RecipeIngredientDetailsResponse,
  RecipeSavePayload,
  Region,
  RecipeVariant,
  UpdateRecipePayload,
  UpdateRecipeVariantPayload,
  RecipeCommentPayload,
  NewCommentPayload,
  AllComments,
  DeleteRecipeIngredientPayload,
  CreateRecipePrototypePayload,
  ApiSuccessResponse,
  RecipeSharePayload,
  ShareRecipeResponse,
  VariantAttachmentPayload,
  VariantAttachmentResponse,
  DownloadPPTXPayload,
  ProjectListResponse,
  HistoryPptListPayload,
  HistoryPptResponse,
  VariantToControlPayload,
  VariantToControlResponse,
  UpdateVariant,
  RecipeAccessorsList,
  UpdateRecipeAccessorsPayload,
  RecipePublishPayload,
} from '../../shared/model/recipes-interface';
import { lastValueFrom } from 'rxjs';
import {
  IngredientSearchResponse,
  CustomIngredientCreatePayload,
  CustomIngredientResponse,
  IngredientNutriInfo,
} from '../../shared/model/ingredient-interface';
import { AuthService } from '../auth';
import { CurrencyExchangeRate } from '../../shared/model/currency-interface';
import { QueryFieldResponse } from '../../shared/model/query-interface';
import { SnackbarService } from '../snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class RecipeService {
  private baseUrl = environment.APP_BASE_URL; //Fix me: Define the base URL as per your API configuration
  private isUser = inject(AuthService);
  private _snackbarService = inject(SnackbarService);
  private _translateService = inject(TranslateService);

  public recipeSearchPayload: RecipeSearchPayload = {
    pageNumber: 1,
    searchUserId: this.isUser.getAccountGroups()?.username ?? '',
    searchIsFinal: false,
    pageSize: 9,
    searchIsAdvanced: false,
  };

  constructor(private _http: HttpClient) {}

  /**
   * Searches for recipes based on the provided payload.
   *
   * @param {*} payload
   * @return {*}
   * @memberof RecipeService
   */
  public searchRecipes(
    payload?: RecipeSearchPayload,
  ): Promise<RecipeSearchResponse | void> {
    return lastValueFrom(
      this._http.post<RecipeSearchResponse>(
        this.baseUrl + CONSTANTS_DETAILS.DRAFT_RECIPE_BASE_URL,
        payload,
      ),
    ).catch(() => {
      this._snackbarService.open({
        message: this._translateService.instant('recipeDataFailed'),
        type: 'error',
      });
    });
  }

  /**
   * Creates a new recipe with the provided payload.
   *
   * @param {RecipeCreatePayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async createRecipe(
    payload: RecipeCreatePayload,
    url: string,
  ): Promise<Recipe> {
    return await lastValueFrom(
      this._http.post<Recipe>(
        this.baseUrl + CONSTANTS_DETAILS.RECIPE_BASE_URL + url,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeCreationError' + err.message,
        ),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Searches for ingredients based on the provided payload.
   *
   * @param {string} payload
   * @returns {Observable<IngredientSearchResponse>}
   */
  public async searchIngredient(
    ingredientName: string,
    regionName?: string,
    countryName?: string,
  ): Promise<IngredientSearchResponse> {
    return await lastValueFrom(
      this._http.post<IngredientSearchResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.SEARCH_INGREDIENT_NAME_FAMILY_URL +
          `?ingredientName=${ingredientName}&regionName=${regionName}&countryName=${countryName}`,
        null,
      ),
    );
  }

  /**
   *
   * @returns {Observable<Region>}
   */
  public async getRegion(): Promise<Region> {
    return await lastValueFrom(
      this._http.get<Region>(this.baseUrl + CONSTANTS_DETAILS.REGION_BASE_URL),
    );
  }

  /**
   *
   * @param regionId
   * @returns {Observable<Country>}
   */
  public async getCountry(regionId: number): Promise<Country> {
    return await lastValueFrom(
      this._http.get<Country>(
        this.baseUrl +
          CONSTANTS_DETAILS.COUNTRY_BASE_URL +
          `?regionId=${regionId}`,
      ),
    );
  }

  /**
   *
   * @returns {Observable<Category>}
   */
  public async getCategory(): Promise<Category> {
    return await lastValueFrom(
      this._http.get<Category>(
        this.baseUrl + CONSTANTS_DETAILS.CATEGORY_BASE_URL,
      ),
    );
  }

  /**
   * Fetches the details of a recipe based on the provided recipe identifier.
   *
   * @param {string} recipeIdentifier
   * @return {*}  {Promise<RecipeDetailsResponse>}
   * @memberof RecipeService
   */
  public async getRecipeDetails(
    recipeIdentifier: string,
  ): Promise<RecipeDetailsResponse> {
    const recipeId = (
      typeof recipeIdentifier === 'string' ? recipeIdentifier : ''
    )
      .normalize('NFC')
      .trim();
    const ID_PATTERN = /^(REC|R)-\d+$/;
    if (!ID_PATTERN.test(recipeId)) {
      const msg = this._translateService.instant('invalidRecipeIdentifier');
      this._snackbarService.open({
        message: msg,
        type: 'error',
      });
      return Promise.reject(new Error(msg as string));
    }
    const base = new URL(
      'api' + CONSTANTS_DETAILS.RECIPE_DETAILS_BASE_URL,
      this.baseUrl,
    );
    base.pathname =
      base.pathname.replace(/\/$/, '') + '/' + encodeURIComponent(recipeId);
    return await lastValueFrom(
      this._http.get<RecipeDetailsResponse>(base.href),
    );
  }

  /**
   *
   * @param {RecipeSavePayload} payload
   * @returns {Observable<ApiResponse>}
   */
  public async saveRecipeDetails(
    payload: RecipeSavePayload,
  ): Promise<RecipeIngredientDetailsResponse> {
    try {
      return await lastValueFrom(
        this._http.put<RecipeIngredientDetailsResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.SAVE_RECIPE_DETAILS_URL,
          payload,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeDetails_recipeSaveErrorMessage',
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Creates a custom ingredient with the provided payload.
   *
   * @param {CustomIngredientCreatePayload} payload
   * @returns {Observable<any>}
   */
  public async createCustomIngredient(
    payload: CustomIngredientCreatePayload,
  ): Promise<CustomIngredientResponse> {
    try {
      return await lastValueFrom(
        this._http.post<CustomIngredientResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
            CONSTANTS_DETAILS.CREATE_CUSTOM_INGREDIENT_URL,
          payload,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'customIngredientErrorMessage',
          {
            message: (err as Error)?.message ?? '',
          },
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Fetches the currency exchange rate from the API.
   *
   * @return {*}  {Observable<CurrencyExchangeRate>}
   * @memberof RecipeService
   */
  public async getCurrencyExchangeRate(): Promise<CurrencyExchangeRate> {
    return await lastValueFrom(
      this._http.get<CurrencyExchangeRate>(
        this.baseUrl + CONSTANTS_DETAILS.CURRENCY_EXCHANGE_RATE_BASE_URL,
      ),
    );
  }

  /**
   * Retrieves advanced search properties for recipes.
   *
   * @return {*}  {Observable<any>}
   * @memberof RecipeService
   */
  public getAdvancedSearchProperties(): Promise<QueryFieldResponse[] | []> {
    return lastValueFrom(
      this._http.get<QueryFieldResponse[]>(
        this.baseUrl + CONSTANTS_DETAILS.ADVANCED_SEARCH_PARAM_API,
      ),
    ).catch(() => {
      this._snackbarService.open({
        message: this._translateService.instant('queryFieldFail'),
        type: 'error',
      });
      return [];
    });
  }

  /**
   * Creates a recipe prototype.
   *
   * @param {CreateRecipePrototypePayload} payload
   * @return {*}  {Promise<RecipeVariant>}
   * @memberof RecipeService
   */
  public async createRecipePrototype(
    payload: CreateRecipePrototypePayload,
  ): Promise<RecipeVariant> {
    try {
      return await lastValueFrom(
        this._http.post<RecipeVariant>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.CREATE_RECIPE_PROTOTYPE_URL,
          payload,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeDetails_variantCreateErrorMessage',
          {
            message: (err as Error)?.message ?? '',
          },
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Updates the recipe name.
   *
   * @param {UpdateRecipePayload} payload
   * @return {*}  {Promise<ApiSuccessResponse>}
   * @memberof RecipeService
   */
  public async updateRecipeName(
    payload: UpdateRecipePayload,
  ): Promise<ApiSuccessResponse> {
    try {
      return await lastValueFrom(
        this._http.put<ApiSuccessResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.UPDATE_RECIPE_NAME_URL,
          payload,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeDetails_recipeNameUpdateErrorMessage',
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Updates the recipe variant name.
   *
   * @param {UpdateRecipeVariantPayload} payload
   * @return {*}  {Promise<ApiSuccessResponse>}
   * @memberof RecipeService
   */
  public async updateVariantName(
    payload: UpdateRecipeVariantPayload,
  ): Promise<ApiSuccessResponse> {
    try {
      return await lastValueFrom(
        this._http.put<ApiSuccessResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.UPDATE_RECIPE_VARIANT_NAME_URL,
          payload,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeDetails_variantNameUpdateErrorMessage',
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Adding new comments for recipe.
   *
   * @param {NewCommentPayload} payload
   * @return {*}  {Observable<any>}
   * @memberof RecipeService
   */
  public async addRecipeComment(
    payload: NewCommentPayload,
  ): Promise<{ isSuccess: boolean }> {
    return await lastValueFrom(
      this._http.post<{ isSuccess: boolean }>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.ADD_NEW_RECIPE_COMMENT_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeCommentAdditionError' + err.message,
        ),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Adding new comments for recipe variant.
   *
   * @param {NewCommentPayload} payload
   * @return {*}  {Observable<any>}
   * @memberof RecipeService
   */
  public async addVariantComment(
    payload: NewCommentPayload,
  ): Promise<{ isSuccess: boolean }> {
    return await lastValueFrom(
      this._http.post<{ isSuccess: boolean }>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.ADD_NEW_VARIANT_COMMENT_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeVariantCommentAdditionError' + err.message,
        ),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Retrieves all comments for recipe/ variant.
   *
   * @param {RecipeCommentPayload} payload
   * @return {*}  {Observable<any>}
   * @memberof RecipeService
   */
  public async fetchComments(
    payload: RecipeCommentPayload,
  ): Promise<AllComments> {
    return await lastValueFrom(
      this._http.post<AllComments>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.FETCH_COMMENTS_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeFetchCommentsError' + err.message,
        ),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Deletes a recipe ingredient.
   *
   * @param {DeleteRecipeIngredientPayload} payload
   * @return {*}  {Promise<ApiSuccessResponse>}
   * @memberof RecipeService
   */
  public async deleteRecipeIngredient(
    payload: DeleteRecipeIngredientPayload,
  ): Promise<ApiSuccessResponse> {
    try {
      return await lastValueFrom(
        this._http.delete<ApiSuccessResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.DELETE_RECIPE_INGREDIENT_URL,
          { body: payload },
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeDetails_ingredientDeleteErrorMessage',
          {
            message: (err as Error)?.message ?? '',
          },
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Deletes a recipe variant.
   *
   * @param {number} recipeVariantId
   * @return {*}  {Promise<ApiSuccessResponse>}
   * @memberof RecipeService
   */
  public async deleteRecipeVariant(
    recipeVariantId: number,
  ): Promise<ApiSuccessResponse> {
    try {
      return await lastValueFrom(
        this._http.delete<ApiSuccessResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.DELETE_RECIPE_VARIANT_URL +
            `/${recipeVariantId}`,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeDetails_variantDeleteErrorMessage',
          {
            message: (err as Error)?.message ?? '',
          },
        ),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Publishes the recipe as final.
   *
   * @param {UpdateRecipePayload} payload
   * @return {*}  {Promise<ApiSuccessResponse>}
   * @memberof RecipeService
   */
  public async publishRecipe(
    payload: RecipePublishPayload,
  ): Promise<ApiSuccessResponse> {
    try {
      return await lastValueFrom(
        this._http.put<ApiSuccessResponse>(
          this.baseUrl +
            CONSTANTS_DETAILS.RECIPE_BASE_URL +
            CONSTANTS_DETAILS.PUBLISH_RECIPE_URL,
          payload,
        ),
      );
    } catch (err) {
      this._snackbarService.open({
        message: this._translateService.instant((err as Error)?.message ?? ''),
        type: 'error',
      });
      throw err;
    }
  }

  /**
   * Shares a recipe with specified accessors.
   *
   * @param {RecipeSharePayload} [payload]
   * @return {*}
   * @memberof RecipeService
   */
  public shareRecipe(payload?: RecipeSharePayload) {
    return lastValueFrom(
      this._http.post<ShareRecipeResponse>(
        this.baseUrl + CONSTANTS_DETAILS.SHARE_RECIPE_URL,
        payload,
      ),
    ).catch(() => {
      this._snackbarService.open({
        message: this._translateService.instant('recipeShareError'),
        type: 'error',
      });
    });
  }

  /**
   * Gets ingredient specific nutritional information.
   *
   * @param {(number | undefined)} ingredientId
   * @return {*}  {Promise<IngredientNutriInfo>}
   * @memberof RecipeService
   */
  public async getIngredientNutritionalInfo(
    ingredientId: number | undefined,
  ): Promise<IngredientNutriInfo> {
    return await lastValueFrom(
      this._http.get<IngredientNutriInfo>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.INGREDIENT_NUTRITIONAL_INFO_URL +
          `/${ingredientId}?isEnabled=true`,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Fetches the list of attachments for a variant.
   *
   * @param {VariantAttachmentPayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async getVariantAttachments(payload: VariantAttachmentPayload) {
    return await lastValueFrom(
      this._http.get<VariantAttachmentResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.VARIANT_ATTACHMENTS_URL +
          `?recipeId=${payload.recipeId}&recipeVariantId=${payload.recipeVariantId}`,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Downloads a variants attachment.
   *
   * @param {VariantAttachmentPayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async downloadVariantAttachment(payload: VariantAttachmentPayload) {
    return await lastValueFrom(
      this._http.get(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.DOWNLOAD_VARIANT_ATTACHMENT_URL +
          `?recipeId=${payload.recipeId}&recipeVariantId=${payload.recipeVariantId}&filename=${encodeURIComponent(payload.filename ?? '')}`,
        {
          responseType: 'blob',
          observe: 'response',
        },
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }
  /**
   * Updates the attachments for a specific recipe variant.
   *
   * @param {number} recipeId
   * @param {number} variantId
   * @param {File[]} files
   * @return {*}
   * @memberof RecipeService
   */
  public updateVariantAttachments(
    recipeId: number,
    variantId: number,
    files: File[],
  ) {
    const formData = new FormData();
    formData.append('RecipeId', recipeId.toString());
    formData.append('RecipeVariantId', variantId.toString());
    files.forEach((file) => formData.append('Files', file));
    return lastValueFrom(
      this._http.put<UpdateVariant>(
        this.baseUrl + CONSTANTS_DETAILS.UPDATE_VARIANT_ATTACHMENTS_URL,
        formData,
      ),
    ).catch(() => {
      this._snackbarService.open({
        message: this._translateService.instant(
          'recipeUpdateVariantAttachmentsError',
        ),
        type: 'error',
      });
    });
  }

  /**
   * Downloads a PowerPoint presentation for a recipe.
   *
   * @param {*} payload
   * @return {*}
   * @memberof RecipeService
   */
  public downloadVariantPPTX(payload: DownloadPPTXPayload) {
    return lastValueFrom(
      this._http.post(
        this.baseUrl + CONSTANTS_DETAILS.EXPORT_VARIANT_PPTX_URL,
        payload,
        {
          responseType: 'blob',
          observe: 'response',
        },
      ),
    ).catch((err: string | string[]) => {
      this._snackbarService.open({
        message: this._translateService.instant(err),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Gets the list of projects to link with recipe during creation.
   *
   * @param {string} projectName
   * @return {*}
   * @memberof RecipeService
   */
  public async getProjectList(projectName: string) {
    const projectNameParam = new HttpParams().set(
      'projectName',
      projectName ?? '',
    );
    return await lastValueFrom(
      this._http.get<ProjectListResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.PROJECT_LIST_URL +
          `?${projectNameParam}`,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Retrives the list of downloaded powerpoint presentations for a recipe.
   *
   * @param {HistoryPptListPayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async getHistoryPPT(payload: HistoryPptListPayload) {
    return await lastValueFrom(
      this._http.get<HistoryPptResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.HISTORY_PPT_URL +
          `?recipeIdentifier=${payload.recipeIdentifier}`,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Downloads a historical powerpoint presentation from the history list of a recipe.
   *
   * @param {HistoryPptListPayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async downloadHistoryPPT(payload: HistoryPptListPayload) {
    return await lastValueFrom(
      this._http.get(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.DOWNLOAD_HISTORY_PPT_URL +
          `?recipeId=${payload.recipeId}&filename=${encodeURIComponent(payload.filename ?? '')}`,
        {
          responseType: 'blob',
          observe: 'response',
        },
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Converts a non-control variant to control type.
   *
   * @param {VariantToControlPayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async convertVariantToControl(payload: VariantToControlPayload) {
    return await lastValueFrom(
      this._http.put<VariantToControlResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.UPDATE_VARIANT_TO_CONTROL_NAME_URL,
        payload,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Retrives a list of recipe accessors for a recipe.
   *
   * @param {string} recipeIdentifier
   * @return {*}
   * @memberof RecipeService
   */
  public async getRecipeAccessors(recipeIdentifier: string) {
    return await lastValueFrom(
      this._http.get<RecipeAccessorsList>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.GET_RECIPE_ACCESSORS_URL +
          `?recipeIdentifier=${recipeIdentifier}`,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Updates the access level of recipe accessors.
   *
   * @param {UpdateRecipeAccessorsPayload} payload
   * @return {*}
   * @memberof RecipeService
   */
  public async updateRecipeAccessors(payload: UpdateRecipeAccessorsPayload) {
    return await lastValueFrom(
      this._http.put<ApiSuccessResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.UPDATE_RECIPE_ACCESSORS_URL,
        payload,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }

  /**
   * Deletes a recipe accessor from the recipe accessor list.
   *
   * @param {number} recipeId
   * @param {string} accessorEmail
   * @return {*}  {Promise<ApiSuccessResponse>}
   * @memberof RecipeService
   */
  public async deleteRecipeAccessor(
    recipeId: number,
    accessorEmail: string,
  ): Promise<ApiSuccessResponse> {
    return await lastValueFrom(
      this._http.delete<ApiSuccessResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.RECIPE_BASE_URL +
          CONSTANTS_DETAILS.DELETE_RECIPE_ACCESSORS_URL +
          `?recipeId=${recipeId}&accessorEmail=${accessorEmail}`,
      ),
    ).catch((err) => {
      const errorMsg =
        err &&
        typeof err === 'object' &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'genericError';
      this._snackbarService.open({
        message: this._translateService.instant(errorMsg),
        type: 'error',
      });
      throw err;
    });
  }
}
